import { Injectable } from '@angular/core';
import { RemoteLoggerService } from './remote-logger.service';

interface ScriptLoaderResult {
  script: string;
  status: string;
  loaded: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private loadedScripts: string[] = [];

  constructor(private logger: RemoteLoggerService) {}

  async loadScripts(scripts: string[]): Promise<void> {
    const promises: Promise<ScriptLoaderResult>[] = [];
    scripts.forEach((script) => promises.push(this.loadScript(script)));

    const result = await Promise.all(promises);
    this.handleScriptsResult(result);
  }

  loadScript(scriptSrc: string): Promise<ScriptLoaderResult> {
    return new Promise<ScriptLoaderResult>((resolve) => {
      const script = this.findScript(scriptSrc);

      if (!script) {
        const scriptElement = document.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.src = scriptSrc;
        scriptElement.async = true;

        scriptElement.onload = () => {
          this.loadedScripts.push(scriptSrc);
          resolve({ script: scriptSrc, loaded: true, status: 'Loaded' });
        };

        scriptElement.onerror = () =>
          resolve({ script: scriptSrc, loaded: false, status: 'Error' });

        document.body.appendChild(scriptElement);
      } else {
        resolve({ script: scriptSrc, loaded: true, status: 'Already Loaded' });
      }
    });
  }

  private handleScriptsResult(result: ScriptLoaderResult[]) {
    if (result.some((res) => !res.loaded)) {
      const failedScripts = result
        .filter((r) => !r.loaded)
        .map((r) => r.script)
        .join(',');
      this.logger.log(
        `One or more scripts failed to load: ${failedScripts}`
      );
    }
  }

  private findScript(scriptSrc: string): string | undefined {
    return this.loadedScripts.find((script) => script === scriptSrc);
  }
}
